import { FormEvent, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useChat } from '../ChatEngine';
import { IconMic, IconPaperclip } from './Icons';

/**
 * Le fil de messages + le composeur, sans rien autour : la page plein écran
 * (`/chat`) et la popin en bas à droite affichent le MÊME composant, branché sur
 * le même état (<ChatProvider>). Passer de l'un à l'autre ne recharge donc rien.
 */
export default function ChatPanel({ maxLines = 8, autoFocus = false }: { maxLines?: number; autoFocus?: boolean }) {
  const chat = useChat();
  const [dragging, setDragging] = useState(false);
  const messagesRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { attachments, dictation, input, messages, streaming } = chat;

  useEffect(() => {
    messagesRef.current?.scrollTo({ top: messagesRef.current.scrollHeight });
  }, [messages]);



  // Auto-grow the composer with its content, up to `maxLines`, then scroll.
  // Runs on every input change — incl. programmatic ones (draft prefill, reset after send).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const cs = getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight) || 21;
    const borders = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    const maxHeight =
      lineHeight * maxLines + (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0) + borders;
    el.style.height = Math.min(el.scrollHeight + borders, maxHeight) + 'px';
    el.style.overflowY = el.scrollHeight + borders > maxHeight ? 'auto' : 'hidden';
  }, [input, maxLines]);

  // La popin s'ouvre pour qu'on y écrive : curseur DERRIÈRE le brouillon pré-rempli
  // (« … Ma demande : »), pas au début — sinon la question tapée se retrouve devant
  // le contexte, et il faut d'abord aller cliquer en bas du champ. Placé après
  // l'auto-grandissement ci-dessus pour que `scrollHeight` soit déjà à jour.
  useEffect(() => {
    if (!autoFocus) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
    el.scrollTop = el.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus, chat.focusToken]);

  function onPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const files = e.clipboardData?.files;
    if (files && files.length > 0) {
      e.preventDefault();
      chat.addFiles(files);
    }
  }
  function onDropFiles(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    chat.addFiles(e.dataTransfer?.files ?? null);
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    chat.send();
  }

  return (
    <>
      <div className="messages" ref={messagesRef}>
        <div className="messages-inner">
          {messages.length === 0 && chat.activeId == null && (
            <div style={{ textAlign: 'center', color: '#999', marginTop: 80 }}>
              Nouvelle conversation — posez votre question ci-dessous.
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`msg ${m.role}`}>
              {m.role === 'assistant' ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
              ) : m.content}
            </div>
          ))}
          {streaming && (
            <div className="msg assistant thinking">
              <div className="thinking-dots"><span /><span /><span /></div>
            </div>
          )}
        </div>
      </div>
      <div
        className={`input-bar ${dragging ? 'dragging' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDropFiles}
      >
        {attachments.length > 0 && (
          <div className="attachments">
            {attachments.map((f, i) => (
              <span key={i} className="att-chip" title={`${f.name} — ${(f.size / 1024).toFixed(1)} kB`}>
                <span className="att-name"><IconPaperclip size={13} /> {f.name}</span>
                <span className="att-size">{(f.size / 1024).toFixed(0)} kB</span>
                <button
                  type="button"
                  className="att-remove"
                  onClick={() => chat.removeAttachment(i)}
                  aria-label={`Retirer ${f.name}`}
                  title="Retirer ce document"
                >
                  ×
                </button>
              </span>
            ))}
            {attachments.length > 1 && (
              <button type="button" className="att-clear" onClick={chat.clearAttachments}>
                Tout retirer
              </button>
            )}
          </div>
        )}
        {dictation.error && <div className="dict-error">{dictation.error}</div>}
        <form onSubmit={submit}>
          <button
            type="button"
            className="attach-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={streaming}
            title="Joindre un document"
          >
            <IconPaperclip size={18} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => { chat.addFiles(e.target.files); e.target.value = ''; }}
          />
          {dictation.supported && (
            <button
              type="button"
              className={`attach-btn mic-btn ${dictation.listening ? 'listening' : ''}`}
              onClick={() => dictation.toggle(input)}
              disabled={streaming}
              aria-pressed={dictation.listening}
              title={dictation.listening ? 'Arrêter la dictée' : 'Dictée vocale'}
            >
              <IconMic size={18} />
            </button>
          )}
          <textarea
            ref={textareaRef}
            placeholder={dragging ? 'Lâche pour joindre le(s) fichier(s)…' : 'Pose ta question à Claude…'}
            value={input}
            onChange={(e) => chat.setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); chat.send(); } }}
            onPaste={onPaste}
            disabled={streaming}
          />
          {streaming ? (
            <button type="button" className="stop-btn" onClick={chat.stop}>Arrêter</button>
          ) : (
            <button disabled={!input.trim() && attachments.length === 0}>Envoyer</button>
          )}
        </form>
      </div>
    </>
  );
}
